/**
 * Public web version of a sent broadcast.
 *
 * Unauthenticated by design — this is the link target for SMS and social
 * derivatives, opened by people who may not have an account. The slug is the
 * only credential, so nothing here sends a token.
 */

import axios from "axios";
import config from "./config";
import { getTenantId } from "./apiHelpers";

// Via `config`, not REACT_APP_API_URL directly: config maps local dev to
// localhost:5000/api/v1 and falls back to the production host when the env
// var is unset. Reading the env var here would produce
// "undefined/public/broadcasts/…" in local dev and anywhere the var is
// missing — which is precisely the environment where a fallback matters.
const BASE_URL = config.BASE_URL;

export interface PublicBroadcast {
  subject: string;
  message: string;
  image_url: string | null;
  sent_at: string | null;
  sent_by_name: string | null;
}

export interface PublicBroadcastResponse {
  success: boolean;
  data?: PublicBroadcast;
  error?: string;
}

export const getPublicBroadcast = async (
  slug: string
): Promise<PublicBroadcastResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/public/broadcasts/${slug}`, {
      headers: { "x-tenant-id": getTenantId() },
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { status?: number } };
    // A bad slug, an unsent broadcast and a revoked link all come back as 404
    // deliberately — the endpoint does not distinguish them, and neither
    // should the caller.
    return {
      success: false,
      error: err.response?.status === 404 ? "not_found" : "error",
    };
  }
};
